import type { ChainedCommands, Editor } from '@tiptap/core'
import type { CanvasTextStyleAttr } from '../ProjectCanvas/utils/canvasTextSchema'

/*
  Die Befehle des **Titelwerkzeugs** (CONTEXT.md „Projekt-Kopf“). Ist im
  Titel etwas markiert, gilt die Einstellung der Markierung, sonst dem ganzen
  Titel (entschieden 2026-09-25) — die Markierung bleibt danach, wie sie war.
*/

export type TitleMark = 'bold' | 'italic' | 'underline' | 'strike'

/**
 * Markiert Schritte der Vorschau: Das Schreibfeld meldet sie nicht nach
 * außen (`ProjectTitleEditor`), sonst landete eine nur überfahrene Schrift
 * beim nächsten Blur in der Datenbank.
 */
export const TITLE_PREVIEW_META = 'titlePreview'

function onTitle(editor: Editor, run: (chain: ChainedCommands) => ChainedCommands, { preview = false } = {}): void {
  const { empty, from, to } = editor.state.selection
  // Ohne `focus()`: Wer im Titelwerkzeug formatiert, soll danach nicht im
  // Schreibfeld landen (gemeldet 2026-09-26). ProseMirror hält die Markierung
  // auch ohne Fokus.
  let chain = editor.chain()
  if (preview) chain = chain.setMeta('addToHistory', false).setMeta(TITLE_PREVIEW_META, true)
  if (empty) chain = chain.selectAll()
  chain = run(chain)
  if (empty) chain = chain.setTextSelection({ from, to })
  chain.run()
}

export function toggleTitleMark(editor: Editor, mark: TitleMark): void {
  onTitle(editor, chain => {
    switch (mark) {
      case 'bold': return chain.toggleBold()
      case 'italic': return chain.toggleItalic()
      case 'underline': return chain.toggleUnderline()
      case 'strike': return chain.toggleStrike()
    }
  })
}

/** Schrift, Größe oder Farbe — `null` heißt „wie der Grundwert“. */
export function setTitleStyle(editor: Editor, attr: CanvasTextStyleAttr, value: string | number | null): void {
  onTitle(editor, chain => chain.setCanvasTextStyle(attr, value))
}

/**
 * Hervorheben in einer Marker-Farbe; `null` nimmt den Marker weg. `look` ist
 * die Form für Neues — dieselbe wie in der Vorschau, sonst spränge der Marker
 * beim Klick in eine andere (gemeldet 2026-09-26). Vorhandene Marker behalten
 * ihre Form ohnehin (`setCanvasHighlight`).
 */
export function setTitleHighlight(editor: Editor, color: string | null, look?: number): void {
  onTitle(editor, chain => (color ? chain.setCanvasHighlight(color, look) : chain.unsetMark('canvasHighlight')))
}

export function shuffleTitleMarkers(editor: Editor): void {
  editor.chain().shuffleCanvasMarkers().run()
}

/** „Zurücksetzen“: nur die Gestaltung des Texts, nie das Icon (CONTEXT.md). */
export function resetTitleFormat(editor: Editor): void {
  onTitle(editor, chain => chain.unsetAllMarks())
}

/** Ob der Titel etwas trägt, das nicht Klartext ist — sonst wird kein HTML gespeichert. */
export function titleHasFormatting(html: string): boolean {
  return /<(strong|em|u|s|mark|span)\b/.test(html)
}

/**
 * Die **Vorschau** beim Überfahren eines Menüeintrags (wie auf der Pinnwand):
 * Der Titel zeigt den Wert schon, bevor er gewählt ist. Gemerkt wird der
 * Stand davor; `restore` stellt ihn wieder her — ohne Spur im Rückgängig.
 */
export function createTitlePreview(editor: Editor, onActiveChange?: (active: boolean) => void) {
  let snapshot: { html: string; from: number; to: number } | null = null

  const put = () => {
    if (!snapshot) return
    const { html, from, to } = snapshot
    snapshot = null
    editor.chain()
      .setMeta('addToHistory', false)
      .setMeta(TITLE_PREVIEW_META, true)
      .setContent(html, { emitUpdate: true })
      .setTextSelection({ from, to })
      .run()
  }

  const restore = () => {
    if (!snapshot) return
    put()
    onActiveChange?.(false)
  }

  const show = (attr: CanvasTextStyleAttr | 'highlight', value: string | number | null, look?: number) => {
    if (snapshot) {
      const keep = snapshot
      put()
      snapshot = keep
    } else {
      const { from, to } = editor.state.selection
      snapshot = { html: editor.getHTML(), from, to }
      onActiveChange?.(true)
    }
    onTitle(editor, chain => {
      if (attr !== 'highlight') return chain.setCanvasTextStyle(attr, value)
      return value ? chain.setCanvasHighlight(String(value), look) : chain.unsetMark('canvasHighlight')
    }, { preview: true })
  }

  return { show, restore, get active() { return snapshot !== null } }
}
